import React, { memo } from 'react';
import './offer-card.css';
import { useTranslations } from 'next-intl';
import { getIsFreeClaimTournamentOffer } from '@wwe-portal/web/utils/offer';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { Button } from '@wwe-portal/ui/components/button/button';
import { useGetUsdToWebCoinRate } from '@wwe-portal/web/providers/queries/webcoin';
import { NOT_READY } from '@wwe-portal/web/utils/constants/offer';
import { cn } from '@wwe-portal/ui/lib/lib';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import TooltipWrapper from '@wwe-portal/ui/features/club-store/milestone/tooltip';
import LoadAnimateButton from '../load-animate-button';
import { tryFormatThousands } from '@wwe-portal/utils/format';
import { getDailyOfferAvailableDays } from '@wwe-portal/web/utils/offer/helper';
/**
 * /**
 * @file
 * Contains getter for the available days of Daily Offer
 * @param schedules {array}
 * @return {string}
 */

/**
 * Render Cash Pack Card Name
 * @param contents {array}
 */
const renderCashPackCardHeader = (contents) => {
  if (contents && Array.isArray(contents) && contents.length) {
    return tryFormatThousands(+contents[0].split('x').join(''));
  }

  return '';
};

function OfferCardMobile({
  data,
  transactionStarted,
  offerType = '',
  hasWebCoinPrice = false,
  hasProgress = false,
  isInModal = false,
  handlePurchase,
  handleInfoModalOpenState,
  price,
  webCoinPrice,
}) {
  const t = useTranslations();

  /**
   * Check if All necessary data exists
   * for calculating Bonus % and Strike-through price
   */

  const isFreeClaimTournamentOffer = getIsFreeClaimTournamentOffer(
    offerType,
    data
  );

  const isDataExistsForStrikeAndBonus =
    data.Price &&
    data.defaultCurrency &&
    data.Price?.[data.defaultCurrency] &&
    data.DiscountPercentage &&
    data.DiscountPercentage > 0;
  const strikeThroughPrice = isDataExistsForStrikeAndBonus
    ? data.Price?.[data.defaultCurrency] / (1 - data.DiscountPercentage / 100)
    : 0;

  const isAuthenticated = useIsAuthenticated();
  const currentUser = useGetCurrentUser();
  const availability = getDailyOfferAvailableDays(data.schedules);
  const { data: usdToWebCoinRateData } = useGetUsdToWebCoinRate();
  const usdToWebCoinRate = usdToWebCoinRateData?.data.data;
  const { isMobile, isHydrated } = useResponsive();

  const isDaily = Boolean(offerType === 'daily');

  const renderRemainingQuantity = () => {
    if (!data.EndDate) return null;
    return (
      <div className="justify-content-end on-mobile-card relative flex w-full">
        <span className="left-count-text flex items-center">
          {' • '}
          {data.StockQuantity >= 1000 ? (
            <span className="icon-infinity-fill body-16" />
          ) : (
            tryFormatThousands(data.StockQuantity)
          )}{' '}
          {t('left')}
        </span>
      </div>
    );
  };

  const renderButton = () => {
    const isWithTooltip =
      !data.PortalAvailability && isDaily && isAuthenticated && currentUser;

    return isWithTooltip ? (
      <TooltipWrapper
        content={
          <div style={{ maxWidth: '200px' }}>
            Arriving for purchase on {availability}{' '}
          </div>
        }
        className="w-full"
      >
        <Button
          disabled={Boolean(
            !data.StockQuantity ||
              data.Availability === NOT_READY ||
              (!data.PortalAvailability &&
                offerType === 'daily' &&
                isAuthenticated &&
                currentUser)
          )}
          onClick={() => {
            return transactionStarted === data.OffersID
              ? null
              : handlePurchase(data);
          }}
          variant={
            ((!data.StockQuantity || data.Availability === NOT_READY) &&
              isAuthenticated &&
              currentUser) ||
            (!data.PortalAvailability &&
              offerType === 'daily' &&
              isAuthenticated)
              ? 'orange'
              : 'blue'
          }
          data-id={`${hasProgress ? 'home_' : ''}shop_offers_button_${data?.OfferSectionID}`}
          className={cn(
            `mx-0 mt-[10px] flex h-[40px] w-full items-center justify-center px-[30px] py-0`,
            {
              'mt-[15px] px-[10px]': hasProgress,
            }
          )}
        >
          {transactionStarted === data.OffersID ? (
            <div className="p-[10px]">
              <LoadAnimateButton bodySize="text-xs" />
            </div>
          ) : (!data.StockQuantity && data.Availability === NOT_READY) ||
            data.Availability === NOT_READY ||
            (!data.PortalAvailability &&
              offerType === 'daily' &&
              isAuthenticated &&
              currentUser) ? (
            <p className="m-0 p-[10px] text-xs font-semibold">
              {t('coming_soon')}
            </p>
          ) : !data.StockQuantity && isAuthenticated && currentUser ? (
            offerType === 'rewards' || isFreeClaimTournamentOffer ? (
              <p className="m-0 p-[10px] text-xs font-semibold">
                {t('claimed')}
              </p>
            ) : (
              <p className="m-0 p-[10px] text-xs font-semibold">
                {t('purchased')}
              </p>
            )
          ) : (
            <p
              className={`mb-0 ml-[5px] text-xs font-semibold ${
                isInModal ? 'py-[10px]' : 'p-[10px]'
              }`}
            >
              {offerType === 'rewards' || isFreeClaimTournamentOffer
                ? t('claim')
                : t('buy')}
            </p>
          )}
        </Button>
      </TooltipWrapper>
    ) : (
      <Button
        disabled={Boolean(
          !data.StockQuantity ||
            data.Availability === NOT_READY ||
            (!data.PortalAvailability &&
              offerType === 'daily' &&
              isAuthenticated &&
              currentUser)
        )}
        onClick={() => {
          return transactionStarted === data.OffersID
            ? null
            : handlePurchase(data);
        }}
        variant={
          ((!data.StockQuantity || data.Availability === NOT_READY) &&
            isAuthenticated &&
            currentUser) ||
          (!data.PortalAvailability && offerType === 'daily' && isAuthenticated)
            ? 'orange'
            : 'blue'
        }
        data-id={`${hasProgress ? 'home_' : ''}shop_offers_button_${data?.OfferSectionID}`}
        className={cn(
          `mx-0 flex h-[40px] w-full items-center justify-center px-[30px]`,
          {
            'mt-[10px] px-[10px]': hasProgress,
          }
        )}
        style={{ marginTop: 10 }}
      >
        {transactionStarted === data.OffersID ? (
          <div className="p-[10px]">
            <LoadAnimateButton bodySize="text-xs" />
          </div>
        ) : (!data.StockQuantity && data.Availability === NOT_READY) ||
          data.Availability === NOT_READY ||
          (!data.PortalAvailability &&
            offerType === 'daily' &&
            isAuthenticated &&
            currentUser) ? (
          <p className="m-0 p-[10px] text-xs font-semibold">{t('not_ready')}</p>
        ) : !data.StockQuantity && isAuthenticated && currentUser ? (
          offerType === 'rewards' || isFreeClaimTournamentOffer ? (
            <p className="m-0 p-[10px] text-xs font-semibold">{t('claimed')}</p>
          ) : (
            <p className="m-0 p-[10px] text-xs font-semibold">
              {t('purchased')}
            </p>
          )
        ) : (
          <p
            className={`mb-0 ml-[5px] text-xs font-semibold ${
              isInModal ? 'py-[10px]' : 'p-[10px]'
            }`}
          >
            {offerType === 'rewards' || isFreeClaimTournamentOffer
              ? t('claim')
              : t('buy')}
          </p>
        )}
      </Button>
    );
  };

  if (!isHydrated) return null;

  return (
    <div className="shop-offer-card__mobile rounded-md p-[10px]">
      <div onClick={() => handleInfoModalOpenState(data)} className="flex">
        {offerType === 'web-coin' ? (
          <div
            className={`shop-daily-card-item-energy ${
              data.hasGlow ? 'shop-daily-card-item-energy-extra' : ''
            }`}
          >
            <img
              src={data.OfferItemImage[0]}
              className="img-fluid shop-daily-card-item-energy-img"
              alt="feature_card_image"
            />
          </div>
        ) : (
          <div
            style={{ backgroundImage: `url(${data.OfferBackgroundImage})` }}
            className="shop-offer-card__background-wrapper mr-[5px] flex items-center justify-center rounded-md"
          >
            {data.OfferItemImage ? (
              Array.isArray(data.OfferItemImage) &&
              data.OfferItemImage.length ? (
                data.OfferItemImage.map((image, index) => (
                  <div key={index} className="flex-column relative flex">
                    <img
                      style={{
                        maxWidth:
                          data.OfferItemImage.length > 1
                            ? !isMobile
                              ? 20
                              : 15
                            : 40,
                        minWidth:
                          data.OfferItemImage.length > 1
                            ? !isMobile
                              ? 20
                              : 15
                            : 40,
                        height:
                          data.OfferItemImage.length > 1
                            ? !isMobile
                              ? 20
                              : 15
                            : 40,
                        marginRight: 4,
                      }}
                      src={image}
                      className={`img-fluid ${!isMobile ? 'mr-[1px]' : ''}`}
                      alt="feature_card_image"
                    />
                  </div>
                ))
              ) : (
                <img
                  style={{
                    maxWidth: 40,
                    minWidth: 40,
                    height: 40,
                  }}
                  src={data.OfferItemImage}
                  alt="feature_card_image"
                />
              )
            ) : null}
          </div>
        )}
        <div className="relative flex w-full items-center">
          <div style={{ flex: 1 }} className="font-weight-medium text-xs">
            {offerType === 'cash-packs'
              ? `${renderCashPackCardHeader(data.OfferItemTexts)} x ${t('cash_pack')}`
              : data.StoreItemName.replace(/<.+?>/g, '')}
            <span
              className={`mx-0 mt-[2px] flex items-center`}
              data-id={
                offerType === 'rewards' || isFreeClaimTournamentOffer
                  ? !data.StockQuantity ||
                    data.Availability === NOT_READY ||
                    (!data.PortalAvailability && offerType === 'daily')
                    ? 'shop_item_claimed_button'
                    : 'shop_item_claim_button'
                  : offerType === 'daily'
                    ? 'shop_item_daily_button'
                    : 'shop_item_standart_button'
              }
            >
              {(!data.StockQuantity ||
                data.Availability === NOT_READY ||
                (!data.PortalAvailability && offerType === 'daily')) &&
              isAuthenticated &&
              currentUser ? (
                <p className="text-text600 m-0 p-0 text-xs">
                  {data.Availability === NOT_READY ||
                  (!data.PortalAvailability && offerType === 'daily')
                    ? t('not_ready')
                    : offerType === 'rewards' || isFreeClaimTournamentOffer
                      ? t('claimed')
                      : t('purchased')}
                </p>
              ) : (
                <>
                  {offerType === 'rewards' || isFreeClaimTournamentOffer ? (
                    <span
                      className={`text-yellow800 mb-0 ml-0 text-xs font-semibold`}
                    >
                      {t('claim')}
                    </span>
                  ) : (
                    <>
                      <div className="orangeText flex items-center">
                        {hasWebCoinPrice ? (
                          <IcoMoonIcon
                            iconName="web-coin-circle"
                            className="text-yellow800 size-3"
                          />
                        ) : (
                          <img src="/assets/icons/money-dollar.svg" />
                        )}
                        <span className={`mb-0 ml-[3px] text-xs font-semibold`}>
                          {`${tryFormatThousands(hasWebCoinPrice ? webCoinPrice : price)} ${
                            hasWebCoinPrice && offerType === 'daily'
                              ? ''
                              : data.defaultCurrency
                          }`}
                        </span>
                      </div>

                      {strikeThroughPrice ? (
                        <div className="text-text400 flex items-center">
                          <span className="strike ml-[1px] text-xs font-semibold underline">
                            {`${tryFormatThousands(strikeThroughPrice.toFixed(2))} ${
                              hasWebCoinPrice && offerType === 'daily'
                                ? ''
                                : data.defaultCurrency
                            }`}
                          </span>
                        </div>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </span>

            {availability && offerType === 'daily' ? (
              <div className="text-text500 text-2xs flex items-center font-normal">
                <span>{t('available')}</span>
                <span className="mb-0 px-[5px]">{availability}</span>
                <span>
                  {' • '}
                  {data.StockQuantity >= 1000 ? (
                    <span className="icon-infinity-fill text-4" />
                  ) : (
                    tryFormatThousands(data.StockQuantity)
                  )}{' '}
                  {t('left')}
                </span>
              </div>
            ) : null}
            {offerType === 'cash-packs' ? (
              <div className="mt-[1px] flex items-center">
                <span className="icon-web-coin-circle text-blue700 text-xs" />
                {data.Price &&
                data.defaultCurrency &&
                data.Price?.[data.defaultCurrency] ? (
                  <p className="shop-daily-card-item__header__web__coin text-text400 mb-0 ml-[5px]">
                    {tryFormatThousands(
                      Math.round(
                        usdToWebCoinRate?.usdToWebCoin ??
                          1 * data.Price?.[data.defaultCurrency]
                      )
                    )}
                    <span className="ml-[5px]">{t('web_coins_bonus')}</span>
                  </p>
                ) : null}
              </div>
            ) : null}
            {offerType !== 'daily' && (
              <div className="shop-offer-card__description">
                {renderRemainingQuantity()}
              </div>
            )}
          </div>
        </div>
      </div>
      {renderButton()}
    </div>
  );
}

export default memo(OfferCardMobile);
